

import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Title, useNotify } from 'react-admin';
import AgGrid from '../../components/ag-grid/ag-grid';
import { dataProvider } from '../../DataProvider';
import DeliveryMethodEditPopUp from './component/delivery-method-edit';
import { formatDateTime, formatLocalTime } from '../../components/ex_dayjs';
import { loadSourceType, loadDeliveryMethod, setDefaultValuesFromURL, autoLoad } from '../../utils/common';
import RenderControls from '../../components/ra-list/RenderControls';
import { FilterListProvider } from '../../context/FilterListProvider';
import ApplyVenuesButton from './component/apply-venue-button';
import ApplyVenuesDialog from './component/apply-venue-dialog';
import BulkActionButtonBar from './component/bulk-action-button-bar';
import BulkDeliveryMethodDialog from './component/BulkDeliveryMethodDialog';
import { LinearProgress } from '@mui/material';

interface IData {
    [key: string]: any;
}

const EventDeliveryList = () => {
    const location = useLocation();
    const notify = useNotify();
    const refInput = useRef(null);
    const refFilter = useRef(null);
    const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
    const [marketType, setMarketType] = useState<any[]>([]);
    const [deliveryMethod, setDeliveryMethod] = useState<any[]>([]);
    const [defaultValues, setDefaultValues] = useState<IData>({});
    const [defaultValuesAgGrid, setDefaultValuesAgGrid] = useState({
        sortField: { field: 'id', order: 'asc' }, // default sort field
    });
    const [applyVenues, setApplyVenues] = useState({ open: false, filterList: null });
    const [isSearchParams, setIsSearchParams] = useState(!!window.location.search);

    /** Rows currently checked in the grid; drives the bulk-action bar visibility. */
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    /** Controls visibility of the bulk delivery-method dialog. */
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
    /** Tracks in-flight bulk-save request so the dialog Save button can be disabled. */
    const [isBulkSaving, setIsBulkSaving] = useState(false);

    let doLoadData = true;
    useEffect(() => {
        if (doLoadData) {
            loadSourceType(dataProvider, (response) => {
                setMarketType(response);
            });
            loadDeliveryMethod(dataProvider, (response) => {
                setDeliveryMethod(response);
            });
            doLoadData = false;
        }
        if (location.search) {
            let newDefaultValue = { ...defaultValues };
            setDefaultValuesFromURL(location.search.replace('SiteId', 'SiteId_eq'), newDefaultValue, controls);
            setDefaultValues(newDefaultValue);
            autoLoad(refFilter);
            setIsSearchParams(false);
        }
    }, []);

    React.useEffect(() => {
        if (!isSearchParams) {
            let _temp =  defaultValues['SiteId_eq'];
            defaultValues.SiteId = _temp;
            delete defaultValues['SiteId_eq'];
            setDefaultValues(defaultValues);
        }
    }, [isSearchParams]);

    React.useEffect(() => {
        if (defaultValues.SiteId)
            handleSearch(defaultValues);
    }, []);

    const defaultColDef = useMemo(() => {
        return {
            sortable: true,
            resizable: true,
            flex: 1,
            minWidth: 50,
            filter: false,
            suppressMovable: true,
            singleClickEdit: true,
        };
    }, []);

    let colDefs = [
        {
            // Checkbox column for bulk selection — no data field, purely for row selection.
            checkboxSelection: true,
            headerCheckboxSelection: true,
            maxWidth: 50,
            sortable: false,
            resizable: false,
        },
        { field: 'type', tooltipField: 'type', headerName: 'Type', maxWidth: 100 },
        { field: 'siteId', tooltipField: 'siteId', headerName: 'Site ID', minWidth: 110 },
        {
            field: 'name', tooltipField: 'name', headerName: 'Event',
            cellRenderer: params => {
                return <a target='_blank' href={`${params.data.url}`} rel="noreferrer" >{params.data.name}</a>;
            }
        },
        { field: 'venue', tooltipField: 'venue', headerName: 'Venue' },
        { field: 'isEdited', tooltipField: 'isEdited', headerName: 'Edited', hide: true },
        {
            field: 'dateTime', tooltipField: 'dateTime', headerName: 'Event Date', maxWidth: 150,
            valueFormatter: params => { return formatDateTime(params.value); },
        },
        {
            field: 'finalDeliveryMethod', headerName: 'Delivery Method', editable: true, minWidth: 300,
            valueFormatter: params => { return deliveryMethod.find((i: any) => (i.id !== '') && i.id === params.value).name; },
            cellStyle: params => {
                if (params.data.isEdited) return { color: 'orange' };
            },
            cellEditor: DeliveryMethodEditPopUp,
            cellEditorParams: { deliveryMethod: deliveryMethod },
            cellEditorPopup: true,
            cellEditorPopupPosition: 'over',
        },
        {
            field: 'updatedDMBy', tooltipField: 'updatedDMBy', headerName: 'Updated Rule By', maxWidth: 170,
        },
        {
            field: 'updatedDMOn', headerName: 'Updated Rule On', maxWidth: 150,
            valueFormatter: params => { return params.value !== null ? formatLocalTime(params.value) : null; },
            tooltipValueGetter: (params) => { return params.value !== null ? formatLocalTime(params.value) : null; },
        },
    ];
    const controls = [
        { component: 'select', name: 'type_eq', label: 'Type', choices: marketType },
        { component: 'textfilter', name: isSearchParams ? 'SiteId_eq' : 'SiteId', label: 'Site ID', xs: 2, searchType: 'exact' },
        { component: 'text', name: 'name', label: 'Event', xs: 2 },
        { component: 'text', name: 'venue', label: 'Venue', xs: 2 },
        { component: 'daterange', name: 'dateTime', label: 'Event Date' },
        { component: 'select', name: 'finalDeliveryMethod_eq', label: 'Delivery Method', choices: deliveryMethod, xs: 1.5 },
        { component: '', name: 'empty', xs: 1.5 },
        {
            component: 'select', name: 'isEdited_eq', label: 'Edited',
            choices: [
                { id: false, name: 'Not Edited' },
                { id: true, name: 'Edited' }
            ]
        },
        { component: 'number', name: 'preferredDeliveryMethodEditID', label: 'Rule ID', xs: 1 },
        { component: 'text', name: 'updatedDMBy', label: 'Updated Rule By' },
        { component: 'daterange', name: 'updatedDMOn', label: 'Updated Rule On', xs: 2.3 }
    ];

    const handleSearch = (filterList) => {
        refInput.current.customFilterSearch(filterList);
    };

    const openApplyVenues = (filterList) => {
        setApplyVenues({ open: true, filterList: filterList });
    };

    /**
     * Fired by the AgGrid `onSelectionChanged` event. Keeps `selectedRows` in
     * sync so the bulk-action bar shows/hides correctly.
     * @param {object} params - The AgGrid selection-changed event params.
     */
    const onSelectionChanged = (params) => {
        setSelectedRows(params.api.getSelectedRows());
    };

    /**
     * Sends the bulk delivery-method update request to the backend.
     * The update is venue-wide: selecting any event from a venue applies the new delivery method
     * to ALL events at that venue. Selected rows are deduplicated by venue before sending,
     * so multiple events from the same venue produce only one rule.
     * On success the affected rows are updated in place and the selection is cleared.
     * @param {number} deliveryMethodId - The delivery method chosen in the dialog.
     */
    const handleBulkSave = (deliveryMethodId) => {
        setIsBulkSaving(true);
        dataProvider.currentUser().then((user: any) => {
            if (user.email) {
                // Deduplicate by venue — one rule per unique venue covers all its events.
                const seenVenues = new Set();
                const uniqueByVenue = selectedRows.filter((row: any) => {
                    if (seenVenues.has(row.venue)) return false;
                    seenVenues.add(row.venue);
                    return true;
                });

                const payload = uniqueByVenue.map((row: any) => ({
                    ...row,
                    deliveryMethodEdit: {
                        apply: 2, // venue-wide scope
                        deliveryMethod: deliveryMethodId,
                        eventId: row.id,
                        id: 0,
                        name: row.name,
                        notes: [],
                        originDeliveryMethod: row.originDeliveryMethod ?? row.finalDeliveryMethod,
                        type: row.type,
                        venue: row.venue,
                    },
                }));

                dataProvider.postData('EventDelivery', 'ApplyDeliveryMethod', payload, 'PUT')
                    .then((response: any) => {
                        if (response.data) {
                            notify('Delivery method updated successfully.', { type: 'success', autoHideDuration: 2000 });

                            // Refresh all rows at the affected venues so the grid reflects the venue-wide rule immediately.
                            const affectedVenues = new Set(selectedRows.map((r: any) => r.venue));
                            const api = refInput.current.getAgGridInstance().api;
                            api.forEachNode((node) => {
                                if (affectedVenues.has(node.data.venue)) {
                                    node.setData({
                                        ...node.data,
                                        preferredDeliveryMethod: deliveryMethodId,
                                        finalDeliveryMethod: deliveryMethodId,
                                        isEdited: true,
                                        updatedDMBy: user.email || '',
                                        updatedDMOn: formatLocalTime(new Date()),
                                    });
                                }
                            });
                            api.deselectAll();
                            setSelectedRows([]);
                        } else {
                            notify(`Update failed: ${response?.message}`, { type: 'error', autoHideDuration: 3000 });
                        }
                    })
                    .catch((err: any) => {
                        notify(`Update failed: ${err}`, { type: 'error', autoHideDuration: 3000 });
                    })
                    .finally(() => {
                        setIsBulkSaving(false);
                        setBulkDialogOpen(false);
                    });
            }
        });
    };

    return (
        <div style={containerStyle}>
            <Title title={'Event Delivery'}></Title>
            {isBulkSaving && <LinearProgress color="primary" sx={{ height: '4px' }} />}
            <FilterListProvider>
                <RenderControls
                    // useHashUrl
                    refFilter={refFilter}
                    defaultValues={defaultValues}
                    controls={controls}
                    onSearch={handleSearch}
                    agSource='EventDelivery'
                    agSort={defaultValuesAgGrid.sortField}
                    actions={<ApplyVenuesButton callback={openApplyVenues} />}
                    exportFile={{
                        filename: `EventDelivery-${formatLocalTime(new Date(), 'YY-MM-DD')}`,
                        columns: ['Type', 'SiteId', 'Name', 'Venue', 'DateTime', 'IsEdited', 'FinalDeliveryMethodName', 'FinalRule', 'UpdatedDMBy', 'UpdatedDMOn', 'Url'],
                        limit: 1000000
                    }}
                />
            </FilterListProvider>

            <BulkActionButtonBar
                selectedCount={selectedRows.length}
                onActionClick={() => setBulkDialogOpen(true)}
            />

            <AgGrid
                ref={refInput}
                list={'EventDelivery'}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                rowHeight={45}
                rowSelection='multiple'
                suppressRowClickSelection={true}
                onSelectionChanged={onSelectionChanged}
                // editType={'fullRow'}
                setDefaultValues={setDefaultValuesAgGrid}
            ></AgGrid>

            {applyVenues.open && <ApplyVenuesDialog applyVenues={applyVenues} setApplyVenues={setApplyVenues} deliveryMethod={deliveryMethod} marketType={marketType} />}

            <BulkDeliveryMethodDialog
                open={bulkDialogOpen}
                selectedCount={selectedRows.length}
                loading={isBulkSaving}
                deliveryMethod={deliveryMethod}
                onClose={() => setBulkDialogOpen(false)}
                onSave={handleBulkSave}
            />
        </div>
    );
};

export default EventDeliveryList;
